import React, { useState, useEffect } from 'react';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import TextArea from '@/UIlibrary/TextArea/TextArea';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useTranslations } from 'use-intl';
import { FincluOperationStatus } from '@/types/FincluOperationStatus';
import { FincluStatusBadge } from './FincluStatusBadge';
import { updateFincluOperationStatus } from '@/lib/fincluOperationStatusApi';
import logger from '@/logger';
import toast from 'react-hot-toast';

interface UpdateFincluStatusModalProps {
	customerId: string;
	currentStatus: FincluOperationStatus;
	customerName: string;
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
	preselectedStatus?: FincluOperationStatus | null;
}

const getValidTransitions = (currentStatus: FincluOperationStatus): FincluOperationStatus[] => {
	const transitions: Record<FincluOperationStatus, FincluOperationStatus[]> = {
		[FincluOperationStatus.IN_PROGRESS]: [],
		[FincluOperationStatus.PENDING_REVIEW]: [
			FincluOperationStatus.APPROVED,
			FincluOperationStatus.REJECTED,
			FincluOperationStatus.PENDING_CLARIFICATION
		],
		[FincluOperationStatus.PENDING_CLARIFICATION]: [
			FincluOperationStatus.APPROVED,
			FincluOperationStatus.REJECTED,
			FincluOperationStatus.PENDING_REVIEW
		],
		[FincluOperationStatus.APPROVED]: [
			FincluOperationStatus.REJECTED,
			FincluOperationStatus.PENDING_CLARIFICATION
		],
		[FincluOperationStatus.REJECTED]: [
			FincluOperationStatus.APPROVED,
			FincluOperationStatus.PENDING_CLARIFICATION
		]
	};
	return transitions[currentStatus] || [];
};

export const UpdateFincluStatusModal: React.FC<UpdateFincluStatusModalProps> = ({
	customerId,
	currentStatus,
	customerName,
	isOpen,
	onClose,
	onSuccess,
	preselectedStatus
}) => {
	const t = useTranslations('SupportClients');
	const [newStatus, setNewStatus] = useState<FincluOperationStatus | null>(null);
	const [deliberation, setDeliberation] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [deliberationError, setDeliberationError] = useState('');

	const validTransitions = getValidTransitions(currentStatus);

	useEffect(() => {
		if (isOpen) {
			const initialStatus =
				preselectedStatus && validTransitions.includes(preselectedStatus)
					? preselectedStatus
					: validTransitions[0] || null;
			setNewStatus(initialStatus);
			setDeliberation('');
			setDeliberationError('');
		}
	}, [isOpen, currentStatus, preselectedStatus]);

	const handleDeliberationChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		setDeliberation(value);
		if (deliberationError) {
			setDeliberationError('');
		}
	};

	const handleSubmit = async () => {
		if (!newStatus) {
			return;
		}

		if (deliberation.length < 10) {
			setDeliberationError(t('MinCharsRequired', { count: 10 - deliberation.length }));
			return;
		}

		setIsLoading(true);
		try {
			await updateFincluOperationStatus({
				customer_id: customerId,
				new_status: newStatus as FincluOperationStatus.APPROVED | FincluOperationStatus.REJECTED,
				deliberation_message: deliberation
			});
			toast.success(t('StatusUpdateSuccess'));
			onSuccess();
			onClose();
		} catch (error) {
			logger.error('Error updating status:', error);
			toast.error(t('StatusUpdateError'));
		} finally {
			setIsLoading(false);
		}
	};

	const deliberationLength = `${deliberation.length}/1000`;

	return (
		<ConfirmModal
			isOpen={isOpen}
			confirmActionText={t('Save')}
			title={t('UpdateFincluStatus')}
			onClose={onClose}
			onCancel={onClose}
			onConfirm={handleSubmit}
			maxWidth={600}
			subtitle={t('UpdateStatusFor', { name: customerName })}
			confirmBtnLoading={isLoading}
		>
			<div className="flex flex-col gap-4 py-4">
				<div>
					<Typography size="md">{t('CurrentStatus')}</Typography>
					<div className="mt-2">
						<FincluStatusBadge status={currentStatus} size="md" />
					</div>
				</div>

				{validTransitions.length === 0 ? (
					<Typography size="sm" color="subtitle">
						{t('NoValidTransitions')}
					</Typography>
				) : (
					<>
						<div>
							<Typography size="md">{t('NewStatus')}</Typography>
							<div className="mt-2 flex gap-2">
								{validTransitions.map((status) => (
									<button
										key={status}
										onClick={() => setNewStatus(status)}
										className={`px-4 py-2 rounded-lg border-2 transition-all ${
											newStatus === status
												? 'border-blue-500 bg-blue-50'
												: 'border-gray-200 hover:border-gray-300'
										}`}
									>
										<FincluStatusBadge status={status} size="sm" />
									</button>
								))}
							</div>
						</div>

						<div>
							<Typography size="md">{t('DeliberationMessage')}</Typography>
							<div className="flex flex-col gap-1 mt-2">
								<TextArea
									rows={4}
									value={deliberation}
									onChange={handleDeliberationChange}
									placeholder={t('DeliberationPlaceholder')}
									error={!!deliberationError}
									hint={deliberationLength}
									maxChars={1000}
									strictMaxChars
								/>
								{deliberationError && (
									<Typography size="sm" color="error">
										{deliberationError}
									</Typography>
								)}
								{!deliberationError && deliberation.length < 10 && (
									<Typography size="sm" color="subtitle">
										{t('MinCharsRequired', { count: 10 - deliberation.length })}
									</Typography>
								)}
							</div>
						</div>
					</>
				)}
			</div>
		</ConfirmModal>
	);
};
